import type {
  CampaignApiRecord,
  GamePassUserEnrollment,
  GamePassUserEnrollmentRecord,
  SeasonApiRecord,
  SeasonApiStatus,
  UserManagerAccordionSeason,
} from "../../types/gamePass";
import { SEASON_API_STATUS } from "../../types/gamePass";
import { unwrapGamePassCollection } from "./responseMappers";

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value != null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const SEASON_STATUS_VALUES = new Set<string>(Object.values(SEASON_API_STATUS));

const readString = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() !== "" ? value : undefined;

const readSeasonStatus = (value: unknown): SeasonApiStatus | undefined => {
  const status = readString(value);
  return status && SEASON_STATUS_VALUES.has(status)
    ? (status as SeasonApiStatus)
    : undefined;
};

const readBoolean = (value: unknown): boolean | undefined =>
  typeof value === "boolean" ? value : undefined;

const readNumber = (value: unknown): number | undefined =>
  typeof value === "number" && Number.isFinite(value) ? value : undefined;

/** Normalize the enrollments listing envelope into a flat array. */
export const normalizeUserEnrollmentsResponse = (
  responseData: unknown,
): GamePassUserEnrollmentRecord[] => {
  const root = asRecord(responseData);
  const dataRecord = asRecord(root?.data);
  const userId =
    readString(dataRecord?.user_id) ?? readString(root?.user_id);
  const enrollments = unwrapGamePassCollection<GamePassUserEnrollmentRecord>(
    (responseData ?? {}) as object,
    "enrollments",
  );

  if (!userId) {
    return enrollments;
  }

  return enrollments.map((entry) => ({
    ...entry,
    user_id: entry.user_id ?? userId,
  }));
};

const resolvePathAvailabilityFlags = (
  record: GamePassUserEnrollmentRecord,
  nestedSeason: Record<string, unknown> | null,
): Pick<
  UserManagerAccordionSeason,
  "free_path_enabled" | "premium_path_enabled"
> => {
  const freePathEnabled =
    readBoolean(nestedSeason?.free_path_enabled) ??
    readBoolean(record.free_path_enabled);
  const premiumPathEnabled =
    readBoolean(nestedSeason?.premium_path_enabled) ??
    readBoolean(record.premium_path_enabled);

  if (freePathEnabled !== undefined || premiumPathEnabled !== undefined) {
    return {
      free_path_enabled: freePathEnabled,
      premium_path_enabled: premiumPathEnabled,
    };
  }

  if (record.is_premium === true) {
    return { free_path_enabled: false, premium_path_enabled: true };
  }

  if (record.is_premium === false) {
    return { free_path_enabled: true, premium_path_enabled: false };
  }

  return {};
};

const buildUserEnrollment = (
  record: GamePassUserEnrollmentRecord,
  seasonId: string,
): Partial<GamePassUserEnrollment> => ({
  season_id: seasonId,
  user_id: readString(record.user_id),
  username: readString(record.username),
  is_premium: readBoolean(record.is_premium),
  enrolled_at: record.enrolled_at ?? record.enrolledAt,
  premium_purchased_at:
    record.premium_purchased_at ?? record.premiumPurchasedAt ?? null,
  current_challenge_id: readString(record.current_challenge_id) ?? "",
  current_challenge_index: readNumber(record.current_challenge_index) ?? 0,
  current_challenge: readString(record.current_challenge) ?? "",
});

const ENROLLMENT_CONTAINER_CAMPAIGN = "CAMPAIGN";
const ENROLLMENT_CONTAINER_SEASON = "SEASON";

const readContainerType = (
  record: GamePassUserEnrollmentRecord | null | undefined,
): string => readString(record?.container_type)?.toUpperCase() ?? "";

/** GP-6: Monthly Challenge rows. Prefer `container_type`; `campaign_id` is
 *  the back-compat signal when the type field has not landed yet. */
export const isCampaignEnrollment = (
  record: GamePassUserEnrollmentRecord | null | undefined,
): boolean => {
  const containerType = readContainerType(record);
  if (containerType === ENROLLMENT_CONTAINER_CAMPAIGN) {
    return true;
  }
  if (containerType === ENROLLMENT_CONTAINER_SEASON) {
    return false;
  }
  return Boolean(readString(record?.campaign_id));
};

const getEnrollmentCampaignId = (
  record: GamePassUserEnrollmentRecord | null | undefined,
): string | undefined => {
  if (!isCampaignEnrollment(record)) {
    return undefined;
  }
  return readString(record?.campaign_id) ?? readString(record?.season_id);
};

type MappedCampaignEnrollment = Omit<CampaignApiRecord, "status"> & {
  status?: CampaignApiRecord["status"];
};

export const mapCampaignEnrollmentToCampaign = (
  record: GamePassUserEnrollmentRecord,
): MappedCampaignEnrollment | null => {
  const campaignId = getEnrollmentCampaignId(record);
  if (!campaignId) {
    return null;
  }
  const enrollmentStatus = readString(record.status)?.toLowerCase() || undefined;
  const startDate = readString(record.start_date);
  const endDate = readString(record.end_date);
  return {
    campaign_id: campaignId,
    id: campaignId,
    name:
      readString(record.season_name) ??
      readString(record.name) ??
      readString(record.current_challenge) ??
      "",
    ...(startDate ? { start_date: startDate } : {}),
    ...(endDate ? { end_date: endDate } : {}),
    ...(enrollmentStatus
      ? { status: enrollmentStatus as CampaignApiRecord["status"] }
      : {}),
  };
};

export const mapCampaignEnrollmentsToCampaigns = (
  enrollments: ReadonlyArray<GamePassUserEnrollmentRecord> = [],
): MappedCampaignEnrollment[] =>
  enrollments
    .filter(isCampaignEnrollment)
    .map(mapCampaignEnrollmentToCampaign)
    .filter((campaign): campaign is MappedCampaignEnrollment => campaign != null);

/** Map a single enrollment record into the accordion season row shape. */
export const mapUserEnrollmentToAccordionSeason = (
  record: GamePassUserEnrollmentRecord,
): UserManagerAccordionSeason | null => {
  const nestedSeason = asRecord(record.season);
  const seasonId =
    readString(record.season_id) ??
    readString(record.id) ??
    readString(nestedSeason?.id) ??
    readString(nestedSeason?.season_id);

  if (!seasonId) {
    return null;
  }

  const name =
    readString(nestedSeason?.name) ??
    readString(record.name) ??
    readString(record.season_name) ??
    "Season";
  const displayName =
    readString(nestedSeason?.display_name) ??
    readString(record.display_name) ??
    readString(record.season_display_name) ??
    null;
  const status =
    readSeasonStatus(nestedSeason?.status) ??
    readSeasonStatus(record.status) ??
    SEASON_API_STATUS.COMPLETED;
  const startDate =
    readString(nestedSeason?.start_date) ??
    readString(record.start_date) ??
    "";
  const endDate =
    readString(nestedSeason?.end_date) ?? readString(record.end_date) ?? "";
  const pathAvailability = resolvePathAvailabilityFlags(record, nestedSeason);

  return {
    id: seasonId,
    season_id: seasonId,
    name,
    display_name: displayName,
    status,
    start_date: startDate,
    end_date: endDate,
    challenge_count:
      readNumber(nestedSeason?.challenge_count) ??
      readNumber(record.challenge_count),
    task_count:
      readNumber(nestedSeason?.task_count) ?? readNumber(record.task_count),
    ...pathAvailability,
    userEnrollment: buildUserEnrollment(record, seasonId),
  };
};

export const mapUserEnrollmentsToAccordionSeasons = (
  enrollments: ReadonlyArray<GamePassUserEnrollmentRecord> = [],
): UserManagerAccordionSeason[] =>
  enrollments
    .filter((record) => !isCampaignEnrollment(record))
    .map(mapUserEnrollmentToAccordionSeason)
    .filter((season): season is UserManagerAccordionSeason => season != null);
